import React, {useState} from 'react';
import styled from 'styled-components';
import ModalComponent from './ModalComponent';
import api from "../api/axiosInstance";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../store/store";
import {logout} from "../reducers/authSlice";
import {errText} from "../api/errors";

interface DeleteAccountModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
}

const ErrorText = styled.p`
    color: #c0392b;
    background: #fdecea;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 12px 0 0;
    font-size: 0.95rem;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
`;

const ConfirmButton = styled.button`
    background: #ff4d4d;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background: #ff0000;
    }
`;

const CancelButton = styled.button`
    background: #ddd;
    color: #333;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background: #ccc;
    }
`;

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onRequestClose }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const user = useSelector((state: RootState) => state.auth.user);
    const dispatch = useDispatch();

    /**
     * Deleting can now fail, and the member has to be told.
     *
     * The server cancels the Stripe subscription before it removes anything, and refuses the whole
     * deletion (503) when Stripe cannot be reached — because the alternative is an account that is
     * gone while the card keeps being charged. Until this release the catch here set `isDeleting`
     * back to false and said nothing, so a refusal looked exactly like a button that had not
     * worked, and the member's only remaining move was to press it again.
     */
    const handleDelete = async () => {
        setIsDeleting(true);
        setError(null);
        try {
            await api.delete(`/account/${user?.id}`);
        } catch (err) {
            setIsDeleting(false);
            setError(errText(err, 'Účet sa nepodarilo odstrániť. Skúste to prosím znova.'));
            return;
        }
        // Log out FIRST, then reload. The other order never ran the dispatch at all: reloading tears
        // the page down, so the store was cleared only by the reload itself and anything persisted
        // outlived the account.
        dispatch(logout());
        window.location.href = '/';
    };

    return (
        <ModalComponent isOpen={isOpen} onClose={onRequestClose} title="Odstraniť účet">
            <p>Ste si istí, že chcete odstrániť svoj účet? Tento krok je nezvratný.</p>
            {error && <ErrorText>{error}</ErrorText>}
            <ButtonContainer>
                <ConfirmButton onClick={handleDelete} disabled={isDeleting}>
                    {isDeleting ? 'Odstraňuje sa…' : 'Áno, odstrániť účet'}
                </ConfirmButton>
                <CancelButton onClick={onRequestClose} disabled={isDeleting}>
                    Nie, ponechať účet
                </CancelButton>
            </ButtonContainer>
        </ModalComponent>
    );
};

export default DeleteAccountModal;
